import React from 'react';
import logo from '../../assets/logo.jpg';
import { useApp } from '../../context/AppContext';
import { fmt } from '../../utils/calc';
import { TAUX } from '../../data/tarifsData';
import './PrintPreview.css';

// ── Convert logo img to base64 for print window ──────────
function imgToBase64(src) {
  return new Promise(resolve => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width  = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext('2d').drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/jpeg', 0.9));
    };
    img.onerror = () => resolve('');
    img.src = src;
  });
}

// ── Build full HTML page for print window ─────────────────
async function buildPrintHTML({ type, meta, lines, totals, c, paiement }) {
  const isF   = type === 'facture';
  const color = isF ? '#0055A5' : '#B8860B';
  const title = isF ? 'FACTURE' : 'DEVIS';
  const logoB64 = await imgToBase64(logo);

  const lineRows = lines.filter(l => l.desc || l.prix > 0).map(l => `
    <tr>
      <td>${l.desc || '—'}</td>
      <td style="text-align:center">${l.qty}</td>
      <td>${l.unit}</td>
      <td style="text-align:right">${Number(l.prix).toLocaleString('fr-FR')} FCFA</td>
      <td style="text-align:right;font-weight:700">${(l.qty * l.prix).toLocaleString('fr-FR')} FCFA</td>
    </tr>`).join('');

  const usd = Math.round(totals.total / TAUX.USD_TO_FCFA).toLocaleString('fr-FR');
  const eur = Math.round(totals.total / TAUX.EUR_TO_FCFA).toLocaleString('fr-FR');

  // Build payment block
  const ops = [
    paiement.mtn    ? `🟡 MTN MoMo : <strong>${paiement.mtn}</strong>`       : null,
    paiement.moov   ? `🔵 Moov Money : <strong>${paiement.moov}</strong>`     : null,
    paiement.celtis ? `🟢 Celtis Cash : <strong>${paiement.celtis}</strong>`  : null,
  ].filter(Boolean).join('<br>');
  const bankLine = (paiement.bank && paiement.bank !== 'Non renseigné (configurer dans Admin > Paiement)')
    ? `<br>🏦 Bancaire : ${paiement.bank.replace(/\n/g, ' · ')}`
    : '';
  const payBlock = ops + bankLine + (paiement.other ? `<br>💵 ${paiement.other}` : '');

  return `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="UTF-8">
<title>${title} N° ${meta.num} — KOTOSTECHNOLOGY</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body {
    font-family: 'Segoe UI', Arial, sans-serif;
    font-size: 13px; color: #111;
    background: white; padding: 24px 30px;
  }
  /* ── Header ── */
  .hdr {
    display: flex; justify-content: space-between; align-items: flex-start;
    padding-bottom: 14px; border-bottom: 3px solid ${color}; margin-bottom: 16px;
  }
  .brand-row { display: flex; align-items: center; gap: 12px; }
  .logo-img  { width: 64px; height: 64px; border-radius: 50%; object-fit: cover; border: 2px solid ${color}; }
  .brand h2  { font-size: 17px; font-weight: 900; color: ${color}; letter-spacing: 1px; margin-bottom: 4px; }
  .brand p   { font-size: 11px; color: #555; line-height: 1.65; }
  .doc-num   { text-align: right; }
  .doc-num h3 { font-size: 26px; font-weight: 900; color: ${color}; }
  .doc-num p  { font-size: 12px; color: #666; margin-top: 3px; }
  /* ── Worker badge ── */
  .worker-badge {
    margin-bottom: 12px; padding: 7px 11px;
    background: #fff8e1; border-left: 4px solid #D4A017;
    font-size: 12px; color: #666;
  }
  .worker-badge strong { color: #B8860B; }
  /* ── Parties ── */
  .parties {
    display: flex; gap: 20px; margin-bottom: 16px;
    padding: 12px 14px; background: #f8f9fa;
    border-radius: 6px;
  }
  .party { flex: 1; }
  .party h4 { font-size: 10px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; color: #999; margin-bottom: 6px; }
  .party p  { font-size: 12px; color: #333; line-height: 1.7; }
  /* ── Table ── */
  table.lines { width: 100%; border-collapse: collapse; margin-bottom: 16px; }
  table.lines thead tr { background: ${color}; }
  table.lines th { color: white; padding: 7px 9px; font-size: 11px; text-align: left; font-weight: 700; }
  table.lines td { padding: 7px 9px; font-size: 12px; border-bottom: 1px solid #eee; }
  table.lines tbody tr:nth-child(even) td { background: #fafafa; }
  /* ── Totals ── */
  .tots { display: flex; justify-content: flex-end; margin-bottom: 16px; }
  table.tot-tbl { border-collapse: collapse; min-width: 260px; }
  table.tot-tbl td { padding: 4px 10px; font-size: 12px; }
  .tot-big td { font-size: 15px; font-weight: 900; color: ${color}; border-top: 2px solid ${color}; padding-top: 8px; }
  .tot-sub    { font-size: 10px; color: #888; display: block; margin-top: 2px; }
  .reste      { color: #c62828; font-weight: 700; }
  /* ── Payment ── */
  .pay-box {
    background: #e8f5e9; border: 1px solid #a5d6a7;
    border-radius: 6px; padding: 10px 14px;
    font-size: 12px; color: #333; line-height: 1.9;
    margin-bottom: 12px;
  }
  .pay-box strong { font-size: 12px; color: #2e7d32; }
  /* ── Notes ── */
  .notes-box {
    background: #fff8e1; border: 1px solid #ffe082;
    border-radius: 6px; padding: 10px 14px;
    font-size: 12px; color: #555; line-height: 1.8;
    margin-bottom: 12px;
  }
  /* ── Footer ── */
  .foot {
    text-align: center; font-size: 11px; color: #999;
    border-top: 1px solid #ddd; padding-top: 10px;
    margin-top: 10px; line-height: 1.8;
  }
  @media print {
    body { padding: 14px 18px; }
    .no-print { display: none !important; }
  }
</style>
</head>
<body>

<!-- HEADER -->
<div class="hdr">
  <div class="brand-row">
    ${logoB64 ? `<img src="${logoB64}" class="logo-img" alt="Logo">` : ''}
    <div class="brand">
      <h2>KOTOSTECHNOLOGY</h2>
      <p>${c.address}<br>${c.tel} · ${c.email}<br>www.kotostechnology.com<br>RCCM : ${c.rccm} · IFU : ${c.ifu}</p>
    </div>
  </div>
  <div class="doc-num">
    <h3>${title}</h3>
    <p><strong>N° ${meta.num}</strong></p>
    <p>Date : ${meta.date}</p>
    ${isF ? '' : `<p>Validité : ${meta.valid} jours</p>`}
  </div>
</div>

<!-- WORKER -->
<div class="worker-badge">
  Établi par : <strong>${meta.worker}</strong> — KOTOSTECHNOLOGY
</div>

<!-- PARTIES -->
<div class="parties">
  <div class="party">
    <h4>Émetteur</h4>
    <p><strong>KOTOSTECHNOLOGY</strong><br>${c.raison}<br>${c.address}<br>Tél : ${c.tel}<br>${c.email}</p>
  </div>
  <div class="party">
    <h4>Client</h4>
    <p><strong>${meta.cnom || '—'}</strong>${meta.ctel ? `<br>Tél : ${meta.ctel}` : ''}${meta.cemail ? `<br>Email : ${meta.cemail}` : ''}${meta.cadr ? `<br>${meta.cadr}` : ''}</p>
  </div>
</div>

<!-- LINES TABLE -->
<table class="lines">
  <thead>
    <tr>
      <th>Description</th>
      <th style="text-align:center;width:50px">Qté</th>
      <th style="width:70px">Unité</th>
      <th style="text-align:right;width:130px">Prix unit.</th>
      <th style="text-align:right;width:130px">Total</th>
    </tr>
  </thead>
  <tbody>${lineRows}</tbody>
</table>

<!-- TOTALS -->
<div class="tots">
  <table class="tot-tbl">
    <tr><td>Sous-total :</td><td style="text-align:right">${fmt(totals.sub)}</td></tr>
    ${totals.remisePct > 0 ? `<tr><td>Remise (${totals.remisePct}%) :</td><td style="text-align:right">– ${fmt(totals.remiseAmt)}</td></tr>` : ''}
    ${totals.tvaPct > 0 ? `<tr><td>TVA (${totals.tvaPct}%) :</td><td style="text-align:right">${fmt(totals.tvaAmt)}</td></tr>` : ''}
    <tr class="tot-big">
      <td>TOTAL ${title} :</td>
      <td style="text-align:right">
        ${fmt(totals.total)}
        <span class="tot-sub">≈ ${usd} USD &nbsp;|&nbsp; ≈ ${eur} EUR</span>
      </td>
    </tr>
    ${isF && totals.acompte > 0 ? `
    <tr><td>Acompte reçu :</td><td style="text-align:right">– ${fmt(totals.acompte)}</td></tr>
    <tr class="reste"><td>Reste à payer :</td><td style="text-align:right">${fmt(totals.restant)}</td></tr>` : ''}
  </table>
</div>

<!-- PAYMENT -->
<div class="pay-box">
  <strong>💳 Modalités de paiement :</strong><br>
  ${payBlock || 'Voir coordonnées de l\'entreprise.'}
</div>

${meta.notes ? `<div class="notes-box"><strong>📝 Notes :</strong><br>${meta.notes.replace(/\n/g, '<br>')}</div>` : ''}

<!-- FOOTER -->
<div class="foot">
  KOTOSTECHNOLOGY – JEHOVA-JIRE MULTI-SERVICES · www.kotostechnology.com<br>
  RCCM N° ${c.rccm} · IFU : ${c.ifu} · Délivré à ${c.rccmVille}<br>
  <strong>Merci de votre confiance !</strong>
</div>

</body>
</html>`;
}

// ── Main exported component ───────────────────────────────
export default function PrintPreview({ type, meta, lines, totals }) {
  const { data } = useApp();
  const isF   = type === 'facture';
  const color = isF ? '#0055A5' : '#B8860B';
  const title = isF ? 'FACTURE' : 'DEVIS';
  const c     = data.contact;

  // Build payment text for screen preview
  const ops = [
    data.paiement.mtn    ? `🟡 MTN MoMo : ${data.paiement.mtn}`     : null,
    data.paiement.moov   ? `🔵 Moov Money : ${data.paiement.moov}`   : null,
    data.paiement.celtis ? `🟢 Celtis Cash : ${data.paiement.celtis}` : null,
  ].filter(Boolean).join('  |  ');
  const bankLine = (data.paiement.bank && !data.paiement.bank.startsWith('Non rens'))
    ? `  |  🏦 ${data.paiement.bank.split('\n')[0]}` : '';
  const payDisplay = (ops + bankLine) || data.paiement.mobile || 'Voir coordonnées entreprise';

  // ── Open real print window ──────────────────────────────
  async function doPrint() {
    const html = await buildPrintHTML({
      type, meta, lines, totals, c, paiement: data.paiement,
    });
    const win = window.open('', '_blank', 'width=820,height=1000');
    if (!win) { alert('Autorisez les popups pour imprimer.'); return; }
    win.document.open();
    win.document.write(html);
    win.document.close();
    // Wait for images to load then print
    win.onload = () => { win.focus(); win.print(); };
    setTimeout(() => { try { win.focus(); win.print(); } catch(e) {} }, 800);
  }

  return (
    <div className="fp-wrap" id="print-zone">
      {/* Print button inside preview */}
      <div className="fp-print-bar">
        <span className="fp-preview-label">👁 Aperçu {title}</span>
        <button className="fp-print-btn" onClick={doPrint}>
          🖨️ Ouvrir &amp; Imprimer
        </button>
      </div>

      {/* Header */}
      <div className="fp-header">
        <div className="fp-logo-area">
          <img src={logo} alt="Logo" className="fp-logo-img" />
          <div className="fp-brand">
            <h2 style={{ color }}>{title} — KOTOSTECHNOLOGY</h2>
            <p>{c.address}<br />{c.tel} · {c.email}<br />RCCM: {c.rccm} · IFU: {c.ifu}</p>
          </div>
        </div>
        <div className="fp-num">
          <h3 style={{ color }}>{title}</h3>
          <p><strong>N° {meta.num}</strong></p>
          <p>Date : {meta.date}</p>
          {!isF && <p>Validité : {meta.valid} jours</p>}
        </div>
      </div>

      <div className="fp-worker">
        <strong>Établi par :</strong> {meta.worker} — KOTOSTECHNOLOGY
      </div>

      <div className="fp-parties">
        <div className="fp-part">
          <h4>Émetteur</h4>
          <p><strong>KOTOSTECHNOLOGY</strong><br />{c.raison}<br />{c.address}<br />Tél : {c.tel}</p>
        </div>
        <div className="fp-part">
          <h4>Client</h4>
          <p><strong>{meta.cnom || '—'}</strong>
            {meta.ctel   && <><br />Tél : {meta.ctel}</>}
            {meta.cemail && <><br />Email : {meta.cemail}</>}
            {meta.cadr   && <><br />{meta.cadr}</>}
          </p>
        </div>
      </div>

      <table className="fp-table">
        <thead>
          <tr style={{ background: color }}>
            <th>Description</th>
            <th style={{textAlign:'center'}}>Qté</th>
            <th>Unité</th>
            <th style={{textAlign:'right'}}>Prix unit.</th>
            <th style={{textAlign:'right'}}>Total</th>
          </tr>
        </thead>
        <tbody>
          {lines.filter(l => l.desc || l.prix > 0).map((l, i) => (
            <tr key={i}>
              <td>{l.desc || '—'}</td>
              <td style={{textAlign:'center'}}>{l.qty}</td>
              <td>{l.unit}</td>
              <td style={{textAlign:'right'}}>{l.prix.toLocaleString('fr-FR')} FCFA</td>
              <td style={{textAlign:'right', fontWeight:700}}>{(l.qty*l.prix).toLocaleString('fr-FR')} FCFA</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="fp-tots">
        <table>
          <tbody>
            <tr><td>Sous-total :</td><td>{fmt(totals.sub)}</td></tr>
            {totals.remisePct > 0 && <tr><td>Remise ({totals.remisePct}%) :</td><td>– {fmt(totals.remiseAmt)}</td></tr>}
            {totals.tvaPct > 0 && <tr><td>TVA ({totals.tvaPct}%) :</td><td>{fmt(totals.tvaAmt)}</td></tr>}
            <tr className="tot-big" style={{color}}>
              <td>TOTAL {title} :</td>
              <td>
                {fmt(totals.total)}<br/>
                <span style={{fontSize:'11px',color:'#888'}}>
                  ≈ {Math.round(totals.total/TAUX.USD_TO_FCFA).toLocaleString('fr-FR')} $ USD &nbsp;|&nbsp;
                  ≈ {Math.round(totals.total/TAUX.EUR_TO_FCFA).toLocaleString('fr-FR')} € EUR
                </span>
              </td>
            </tr>
            {isF && totals.acompte > 0 && <>
              <tr><td>Acompte reçu :</td><td>– {fmt(totals.acompte)}</td></tr>
              <tr style={{color:'#c62828',fontWeight:700}}><td>Reste à payer :</td><td>{fmt(totals.restant)}</td></tr>
            </>}
          </tbody>
        </table>
      </div>

      <div className="fp-pay">
        <strong>💳 Modalités de paiement :</strong><br />
        {payDisplay}
      </div>

      {meta.notes && (
        <div className="fp-notes">
          <strong>📝 Notes :</strong><br />
          {meta.notes.split('\n').map((l, i) => <span key={i}>{l}<br /></span>)}
        </div>
      )}

      <div className="fp-foot">
        KOTOSTECHNOLOGY · www.kotostechnology.com<br />
        RCCM N° {c.rccm} · IFU : {c.ifu} · Délivré à {c.rccmVille}<br />
        Merci de votre confiance !
      </div>
    </div>
  );
}
